import { RouteDtoModelType, RouteTimelineDtoModelType, VisitDtoModelType } from '../../../../store/mst/models';
import { EventType } from '../constants';

export interface StopSelectionAnchor {
  routeId: string;
  stopId: string;
}

export type StopSelectionAnchorsByRoute = Record<string, string>;

interface DeliveryStopSelectionOptions {
  route?: RouteDtoModelType;
  stopId?: string;
  anchorsByRoute?: StopSelectionAnchorsByRoute;
  selectedStops?: RouteTimelineDtoModelType[];
  selectedUnscheduledOrders?: VisitDtoModelType[];
  selectedSingleStop?: RouteTimelineDtoModelType;
  event?: { shiftKey?: boolean; ctrlKey?: boolean; metaKey?: boolean };
}

interface ApplyDeliveryStopSelectionOptions extends DeliveryStopSelectionOptions {
  selectSingleStop: () => void;
  clearSingleStop: () => void;
  setSelectedItems: (items: { stops: string[]; unscheduledOrders: string[] }) => void;
  setSelectedStopAnchorsByRoute: (anchorsByRoute?: StopSelectionAnchorsByRoute) => void;
}

interface ResolvedDeliveryStopSelectionOptions {
  route: RouteDtoModelType;
  routeId: string;
  stopId: string;
  anchorsByRoute: StopSelectionAnchorsByRoute;
  selectedStops: RouteTimelineDtoModelType[];
  selectedUnscheduledOrderIds: string[];
  selectedSingleStop?: RouteTimelineDtoModelType;
  nextAnchorsByRoute: StopSelectionAnchorsByRoute;
}

export const hasRangeSelectionModifier = (
  event?: DeliveryStopSelectionOptions['event'],
): boolean => !!event?.shiftKey;

export const hasToggleSelectionModifier = (
  event?: DeliveryStopSelectionOptions['event'],
): boolean => !!(event?.ctrlKey || event?.metaKey);

const getUnscheduledOrderIds = (selectedUnscheduledOrders: VisitDtoModelType[] = []): string[] => (
  selectedUnscheduledOrders
    .map((order) => order.id)
);

export const getRouteDeliveryStopIds = (route?: RouteDtoModelType): string[] => (
  route?.timeline
    ?.filter((event: RouteTimelineDtoModelType) => event.eventType === EventType.DELIVERING_STOP)
    .map((event: RouteTimelineDtoModelType) => event.id) ?? []
);

export const getDeliveryStopRangeIds = (
  route: RouteDtoModelType | undefined,
  anchor: StopSelectionAnchor | undefined,
  stopId: string | undefined,
): string[] => {
  if (!route || !anchor || !stopId || anchor.routeId !== route.id) {
    return stopId ? [stopId] : [];
  }

  const stopIds = getRouteDeliveryStopIds(route);
  const anchorIndex = stopIds.indexOf(anchor.stopId);
  const stopIndex = stopIds.indexOf(stopId);

  if (anchorIndex < 0 || stopIndex < 0) {
    return [stopId];
  }

  const rangeStart = Math.min(anchorIndex, stopIndex);
  const rangeEnd = Math.max(anchorIndex, stopIndex);

  return stopIds.slice(rangeStart, rangeEnd + 1);
};

const isSelectedStopOnRoute = (
  stop: RouteTimelineDtoModelType,
  route?: RouteDtoModelType,
): boolean => {
  if (!route) {
    return false;
  }

  const stopRouteId = stop.route?.id;
  if (stopRouteId) {
    return stopRouteId === route.id;
  }

  return getRouteDeliveryStopIds(route).includes(stop.id);
};

const getOrderedSelectedStopIds = (
  selectedStops: RouteTimelineDtoModelType[],
  route: RouteDtoModelType,
  selectedStopIds: Set<string>,
): string[] => {
  const otherRouteSelectedStopIds = selectedStops
    .filter((stop) => !isSelectedStopOnRoute(stop, route))
    .map((stop) => stop.id)
    .filter((id) => selectedStopIds.has(id));
  const routeSelectedStopIds = getRouteDeliveryStopIds(route)
    .filter((id) => selectedStopIds.has(id));

  return [...otherRouteSelectedStopIds, ...routeSelectedStopIds];
};

const getSelectedStopIdsOutsideRoute = (
  selectedStops: RouteTimelineDtoModelType[] = [],
  route?: RouteDtoModelType,
): string[] => (
  selectedStops
    .filter((stop) => !isSelectedStopOnRoute(stop, route))
    .map((stop) => stop.id)
);

const getSelectedStopIds = (selectedStops: RouteTimelineDtoModelType[] = []): string[] => (
  selectedStops
    .map((stop) => stop.id)
);

const getValidRouteAnchor = (
  anchorsByRoute: StopSelectionAnchorsByRoute | undefined,
  route: RouteDtoModelType | undefined,
): StopSelectionAnchor | undefined => {
  const routeId = route?.id;
  const anchorStopId = routeId ? anchorsByRoute?.[routeId] : undefined;

  if (!routeId || !anchorStopId || !getRouteDeliveryStopIds(route).includes(anchorStopId)) {
    return undefined;
  }

  return {
    routeId,
    stopId: anchorStopId,
  };
};

const getSelectedSingleStopAnchor = (
  selectedSingleStop: RouteTimelineDtoModelType | undefined,
  route: RouteDtoModelType | undefined,
): StopSelectionAnchor | undefined => {
  if (!route || selectedSingleStop?.route?.id !== route.id) {
    return undefined;
  }

  if (!getRouteDeliveryStopIds(route).includes(selectedSingleStop.id)) {
    return undefined;
  }

  return {
    routeId: route.id,
    stopId: selectedSingleStop.id,
  };
};

const getIncompleteStopSelection = (
  selectedUnscheduledOrderIds: string[],
  nextAnchorsByRoute: StopSelectionAnchorsByRoute,
) => ({
  shouldSelectSingleStop: false,
  selectedStopIds: [],
  selectedUnscheduledOrderIds,
  nextAnchorsByRoute,
});

// Plain click opens the clicked stop details and clears any bulk stop or unscheduled selection.
const getPlainClickStopSelection = (routeId: string, stopId: string) => ({
  shouldSelectSingleStop: true,
  selectedStopIds: [],
  selectedUnscheduledOrderIds: [],
  nextAnchorsByRoute: { [routeId]: stopId },
});

// Shift-click selects a contiguous range on the clicked route while keeping selections on other routes.
const getRangeStopSelection = ({
  route,
  routeId,
  stopId,
  anchorsByRoute,
  selectedStops,
  selectedUnscheduledOrderIds,
  selectedSingleStop,
  nextAnchorsByRoute,
}: ResolvedDeliveryStopSelectionOptions) => {
  const validRouteAnchor = getValidRouteAnchor(anchorsByRoute, route);
  const fallbackAnchor = getSelectedSingleStopAnchor(selectedSingleStop, route);
  const effectiveAnchor = validRouteAnchor ?? fallbackAnchor;
  const routeRangeIds = getDeliveryStopRangeIds(route, effectiveAnchor, stopId);
  const otherRouteSelectedStopIds = getSelectedStopIdsOutsideRoute(selectedStops, route);
  const nextAnchorStopId = effectiveAnchor?.stopId ?? stopId;

  return {
    shouldSelectSingleStop: false,
    selectedStopIds: [...otherRouteSelectedStopIds, ...routeRangeIds],
    selectedUnscheduledOrderIds,
    nextAnchorsByRoute: {
      ...nextAnchorsByRoute,
      [routeId]: nextAnchorStopId,
    },
  };
};

const getSelectedStopsIncludingSingleStop = (
  selectedStops: RouteTimelineDtoModelType[],
  selectedSingleStop?: RouteTimelineDtoModelType,
): RouteTimelineDtoModelType[] => {
  if (!selectedSingleStop || getSelectedStopIds(selectedStops).includes(selectedSingleStop.id)) {
    return selectedStops;
  }

  return [selectedSingleStop, ...selectedStops];
};

const getAnchorsIncludingSingleStop = (
  nextAnchorsByRoute: StopSelectionAnchorsByRoute,
  selectedSingleStop?: RouteTimelineDtoModelType,
): StopSelectionAnchorsByRoute => {
  const selectedSingleStopId = selectedSingleStop?.id;
  const selectedSingleStopRouteId = selectedSingleStop?.route?.id;

  if (!selectedSingleStopId || !selectedSingleStopRouteId || nextAnchorsByRoute[selectedSingleStopRouteId]) {
    return nextAnchorsByRoute;
  }

  return {
    ...nextAnchorsByRoute,
    [selectedSingleStopRouteId]: selectedSingleStopId,
  };
};

const getToggleAnchors = (
  route: RouteDtoModelType,
  routeId: string,
  stopId: string,
  selectedStopIds: Set<string>,
  nextAnchorsByRoute: StopSelectionAnchorsByRoute,
): StopSelectionAnchorsByRoute => {
  const routeStopIds = getRouteDeliveryStopIds(route);
  const routeSelectedIds = routeStopIds.filter((id) => selectedStopIds.has(id));
  const currentRouteAnchorStopId = nextAnchorsByRoute[routeId];

  if (routeSelectedIds.length === 0) {
    const remainingAnchors = { ...nextAnchorsByRoute };
    delete remainingAnchors[routeId];
    return remainingAnchors;
  }

  if (!currentRouteAnchorStopId || currentRouteAnchorStopId === stopId || !routeStopIds.includes(currentRouteAnchorStopId)) {
    return {
      ...nextAnchorsByRoute,
      [routeId]: routeSelectedIds[0],
    };
  }

  return nextAnchorsByRoute;
};

// Ctrl/Cmd-click toggles one stop in the bulk selection and keeps unscheduled orders selected.
const getToggleStopSelection = ({
  route,
  routeId,
  stopId,
  selectedStops,
  selectedUnscheduledOrderIds,
  selectedSingleStop,
  nextAnchorsByRoute,
}: ResolvedDeliveryStopSelectionOptions) => {
  const selectedStopReferences = getSelectedStopsIncludingSingleStop(selectedStops, selectedSingleStop);
  const selectedStopIds = new Set(getSelectedStopIds(selectedStopReferences));
  let nextAnchors = getAnchorsIncludingSingleStop(nextAnchorsByRoute, selectedSingleStop);

  if (selectedStopIds.has(stopId)) {
    selectedStopIds.delete(stopId);
    nextAnchors = getToggleAnchors(route, routeId, stopId, selectedStopIds, nextAnchors);
  } else {
    selectedStopIds.add(stopId);
    nextAnchors = {
      ...nextAnchors,
      [routeId]: stopId,
    };
  }

  return {
    shouldSelectSingleStop: false,
    selectedStopIds: getOrderedSelectedStopIds(selectedStopReferences, route, selectedStopIds),
    selectedUnscheduledOrderIds,
    nextAnchorsByRoute: nextAnchors,
  };
};

export const getNextDeliveryStopSelection = ({
  route,
  stopId,
  anchorsByRoute = {},
  selectedStops = [],
  selectedUnscheduledOrders = [],
  selectedSingleStop,
  event,
}: DeliveryStopSelectionOptions) => {
  const routeId = route?.id;
  const selectedUnscheduledOrderIds = getUnscheduledOrderIds(selectedUnscheduledOrders);
  const nextAnchorsByRoute = { ...anchorsByRoute };

  if (!route || !routeId || !stopId) {
    return getIncompleteStopSelection(selectedUnscheduledOrderIds, nextAnchorsByRoute);
  }

  if (!hasRangeSelectionModifier(event) && !hasToggleSelectionModifier(event)) {
    return getPlainClickStopSelection(routeId, stopId);
  }

  const resolvedSelectionOptions: ResolvedDeliveryStopSelectionOptions = {
    route,
    routeId,
    stopId,
    anchorsByRoute,
    selectedStops,
    selectedUnscheduledOrderIds,
    selectedSingleStop,
    nextAnchorsByRoute,
  };

  if (hasRangeSelectionModifier(event)) {
    return getRangeStopSelection(resolvedSelectionOptions);
  }

  return getToggleStopSelection(resolvedSelectionOptions);
};

export const applyDeliveryStopSelection = ({
  selectSingleStop,
  clearSingleStop,
  setSelectedItems,
  setSelectedStopAnchorsByRoute,
  ...selectionOptions
}: ApplyDeliveryStopSelectionOptions) => {
  const nextSelection = getNextDeliveryStopSelection(selectionOptions);

  if (nextSelection.shouldSelectSingleStop) {
    setSelectedItems({ stops: [], unscheduledOrders: [] });
    setSelectedStopAnchorsByRoute(nextSelection.nextAnchorsByRoute);
    selectSingleStop();
    return nextSelection;
  }

  clearSingleStop();
  setSelectedStopAnchorsByRoute(nextSelection.nextAnchorsByRoute);
  setSelectedItems({
    stops: nextSelection.selectedStopIds,
    unscheduledOrders: nextSelection.selectedUnscheduledOrderIds,
  });

  return nextSelection;
};
